import React, { useEffect } from 'react';
import { PersonDataContext } from '../../contexts/person-data-context';
import ClaimCard, { trackerStage } from '../base/claim-card';
import Button from '../base/button';
import { isDueTask, isReceivedTask } from '../tasks/utils';
import { KenticoDataContext } from '../../contexts/kentico-data-context';
import * as dm from '../../data-models';
import { trackFSVars, trackGA4Event } from '../../utils/analytics';
import NoClaimsView from './no-claims';
import { TaskType } from '../tasks';
import { enums, get, showPaymentPageBasedOnEmployerId } from '../../utils';
import { styledButtons } from '../../components/base/button';
import { PreferencesContext } from '../../contexts/preferences-context';
import {
  GridContainer,
  ColumnContainer,
  StyledLink,
} from '../base/tw-components';
import { useLocation } from 'react-router-dom';
import TemporaryClaimCard from '../base/claim-card/temp-claim-card';
import dayjs from 'dayjs';
import FilterIconNoResults from '../../images/filter-icon-no-results.svg';
import { FilterModal } from './filter-modal';
import BlueFilterIcon from '../../images/blue-filter.svg';

export type RedirectState = {
  ntnToOpen?: string | number;
  tabToOpen?: string;
};

type Props = {
  tasks: TaskType[];
  remoteCfg: { [name: string]: string };
  isEmployerEnabled: boolean;
  vbCoverageData: dm.ICoverageV1ResponseExt;
  failedWithData?: boolean;
  absenceNotifications?: dm.IAbsenceNotifications;
  allTasks?: any;
  ptoChoice?: string;
};

type FontProps = {
  children: React.ReactNode;
  className?: string;
};

type ExternalLinkProps = {
  children: React.ReactNode;
  href: string;
  onClick?: () => void;
};

type SeparatorProps = {
  color: 'blue' | 'green';
};

type ChildProps = {
  children: React.ReactNode;
};

type ButtonModProps = {
  id: string;
  buttonStyle?: keyof typeof styledButtons;
  onClick?: () => void;
  className?: string;
  children?: React.ReactNode;
  [key: string]: any;
};

export const MenuSection = ({ children }: ChildProps) => {
  return (
    <div className="tw-flex-1 tw-basis-full sm:tw-basis-1/3">{children}</div>
  );
};

export const ListContainer = ({ children }: ChildProps) => {
  return <ul className="tw-list-none tw-m-0 tw-p-0">{children}</ul>;
};

export const ListItem = ({ children }: ChildProps) => {
  return <li className="tw-mb-2.5">{children}</li>;
};

export const Title = ({ children, className = '' }: FontProps) => {
  return (
    <h1
      className={`tw-text-2xl md:tw-text-4xl tw-font-normal tw-text-unm-interactive01-800 tw-mb-5 tw-leading-10 ${className}`}
    >
      {children}
    </h1>
  );
};

export const H2 = ({ children, className = '' }: FontProps) => {
  return (
    <h2
      className={`tw-text-sm md:tw-text-lg lg:tw-text-2xl tw-font-medium ${className}`}
    >
      {children}
    </h2>
  );
};

export const SidebarTitle = ({ children, className = '' }: FontProps) => {
  return <H2 className={`${className}`}>{children}</H2>;
};

export const StyledExternalLink = ({
  children,
  href,
  onClick,
}: ExternalLinkProps) => {
  return (
    <a
      href={href}
      className="tw-text-sm md:tw-text-base tw-font-semibold tw-text-unm-interactive01-400 tw-no-underline"
      onClick={onClick}
    >
      {children}
    </a>
  );
};

const ButtonMod = ({
  id,
  buttonStyle,
  onClick,
  className,
  children,
  ...others
}: ButtonModProps) => {
  return (
    <Button
      id={id}
      buttonStyle={buttonStyle}
      onClick={onClick}
      className={`text-base hover:no-underline ${className}`}
      {...others}
    >
      {children}
    </Button>
  );
};

const NoFilteredClaimsView = () => {
  return (
    <div className="tw-z-10 tw-p-8 tw-mb-8 md:tw-p-40 tw-bg-white tw-shadow-lg tw-rounded-lg tw-flex tw-flex-col tw-items-center tw-gap-4">
      <img
        src={FilterIconNoResults}
        alt="No results"
        className="tw-w-24 tw-h-24"
      />
      <span className="tw-text-xl tw-text-neutral-600 tw-font-semibold">
        No Results Found
      </span>
      <p className="tw-text-lg tw-m-0 tw-text-unm-adhoc2 tw-text-center">
        We couldn't find any items matching your current filter selection. Try
        adjusting or clearing your filters to see more results.
      </p>
    </div>
  );
};

export const Separator = ({ color }: SeparatorProps) => {
  const colorClass =
    color === 'blue' ? 'tw-bg-unm-interactive01-200' : 'tw-bg-unm-approved';
  return (
    <hr
      className={`tw-w-36 sm:tw-w-full lg:tw-w-20 tw-h-1 ${colorClass} tw-my-3 tw-ml-0 tw-rounded-lg tw-border-none`}
    />
  );
};

const getShowPaymentInfo = async (
  setShowPaymentInfo: Function,
  ERContent: any,
) => {
  const showInfo = await showPaymentPageBasedOnEmployerId(ERContent);
  setShowPaymentInfo(showInfo);
};

const Summary = ({
  tasks,
  remoteCfg,
  isEmployerEnabled,
  vbCoverageData,
  failedWithData,
  absenceNotifications,
  allTasks,
  ptoChoice,
}: Props) => {
  const location = useLocation();
  const redirectLocation = (location.state as RedirectState) ?? '';
  const caseFromQuery = new URLSearchParams(location.search).get('id') ?? '';
  const ntnToOpen = String(
    (location.state as RedirectState)?.ntnToOpen ?? caseFromQuery,
  );
  const { personData } = React.useContext(PersonDataContext);
  const { callCenter } = React.useContext(KenticoDataContext);
  const { ERContent } = React.useContext(PreferencesContext);
  const hasCoverage = vbCoverageData.hasCoverage;
  const [showPayments, setShowPayments] = React.useState(false);
  const [filterState, setFilterState] = React.useState(
    {} as dm.ClaimCardFilterObject,
  );
  const [totalFilterCount, setTotalFilterCount] = React.useState(0);
  const [filterModalOpen, setFilterModalOpen] = React.useState(false);
  const [showChatClosedTooltip, setShowChatClosedTooltip] =
    React.useState(false);

  const filterClaimCards = (
    parentNTNs: any[],
    filterObject: dm.ClaimCardFilterObject,
  ) => {
    let filteredClaims = parentNTNs;

    const { leaveReasons, minStartDate, maxEndDate, trackerStages } =
      filterObject;

    if (filteredClaims.length > 0 && leaveReasons && leaveReasons.length > 0) {
      filteredClaims = filteredClaims.filter((ntn) => {
        return ntn.leaves?.[0] && leaveReasons.includes(ntn.leaves[0].reason);
      });
    }

    if (filteredClaims.length > 0 && minStartDate) {
      filteredClaims = filteredClaims.filter((ntn) => {
        return ntn.leaves?.[0] && minStartDate <= ntn.leaves[0].startDate;
      });
    }

    if (filteredClaims.length > 0 && maxEndDate) {
      filteredClaims = filteredClaims.filter((ntn) => {
        return ntn.leaves?.[0] && maxEndDate >= ntn.leaves[0].endDate;
      });
    }

    if (
      filteredClaims.length > 0 &&
      trackerStages &&
      trackerStages.length > 0
    ) {
      filteredClaims = filteredClaims.filter((ntn) => {
        const c = absenceNotifications?.absenceNotifications.find(
          (n) => n.notificationCaseId === ntn.NTNId,
        );
        const claimTrackerStage = trackerStage(
          c,
          ntn,
          dayjs().format('YYYY-MM-DD'),
          get(personData, 'documents.letters', []),
        );
        return ntn.leaves?.[0] && trackerStages.includes(claimTrackerStage);
      });
    }

    return filteredClaims;
  };

  const filteredClaims = filterClaimCards(
    personData?.generated?.parentNTNs ?? [],
    filterState,
  );

  const ERTasks = tasks.filter(
    (t: dm.Task) =>
      isDueTask(t?.taskStatusId) && t.assigneeId === 1 && t.taskStatusId !== 10,
  );

  const EETasks = tasks.filter(
    (t: dm.Task) => isDueTask(t?.taskStatusId) && t.assigneeId === 2,
  );

  const allReceivedTasks = tasks.filter(
    (t: dm.Task) => isReceivedTask(t?.taskStatusId) && t.assigneeId === 2,
  );

  const mufmLink = remoteCfg
    ? remoteCfg[enums.RemoteConfigKeys.iServicesIDPURL]
    : '#';

  useEffect(() => {
    if (personData?.person?.companyCode) {
      const coverageNames =
        vbCoverageData.coverages?.map((c) => c.productDescription) || [];
      coverageNames.sort((a, b) => a.localeCompare(b));

      trackFSVars({
        vbCoverages: coverageNames.join(', '),
        vbEmployerEnabled: isEmployerEnabled,
      });
    }
  }, [
    isEmployerEnabled,
    personData?.person?.companyCode,
    vbCoverageData.coverages,
  ]);

  useEffect(() => {
    getShowPaymentInfo(setShowPayments, ERContent);
  }, [ERContent]);

  return personData?.generated?.parentNTNs.length > 0 ||
    (personData?.claimsInProcess && personData?.claimsInProcess?.length > 0) ? (
    <GridContainer id="has-claims-view">
      <ColumnContainer id="summary-section" cols={9}>
        <div className="tw-relative">
          <Title>{`Welcome back, ${personData?.person?.firstName}!`}</Title>
          <div className="tw-m-4 tw-flex tw-gap-4">
            <button
              className="tw-flex tw-items-center tw-text-unm-primary02-400 tw-font-semibold tw-p-0 tw-bg-transparent tw-border-none"
              onClick={() => setFilterModalOpen(!filterModalOpen)}
            >
              <img
                src={BlueFilterIcon}
                alt="Filter Icon"
                className="tw-w-4 tw-h-4 tw-mr-2"
              />
              <p className="tw-m-0">Filters ({totalFilterCount})</p>
            </button>
            <button
              className="tw-text-unm-primary02-400 tw-p-0 tw-bg-transparent tw-border-none"
              onClick={() => {
                setFilterState({} as dm.ClaimCardFilterObject);
                setTotalFilterCount(0);
                setFilterModalOpen(false);
              }}
            >
              <u>
                <p className="tw-m-0">Clear all</p>
              </u>
            </button>
          </div>

          {filterModalOpen && (
            <FilterModal
              setFilterModalOpen={setFilterModalOpen}
              filterState={filterState}
              setFilterState={setFilterState}
              setTotalFilterCount={setTotalFilterCount}
              NTNs={personData?.generated?.parentNTNs || []}
            />
          )}
          {personData?.claimsInProcess?.map(
            (claim: dm.ClaimInProcess, index: number) => (
              <TemporaryClaimCard
                key={`temporary-claim-card-${index}`}
                claim={claim}
                keyId={index}
              />
            ),
          )}
          {filteredClaims.length > 0 ? (
            filteredClaims.map((claim: any, index: number) => (
              <div
                key={`claim-card-${claim?.NTNId}`}
                className="tw-relative tw-mb-8"
              >
                <ClaimCard
                  keyId={index}
                  claim={claim}
                  ERTasks={ERTasks}
                  EETasks={EETasks}
                  failedWithData={failedWithData}
                  allReceivedTasks={allReceivedTasks}
                  absenceNotifications={absenceNotifications}
                  personData={personData}
                  allTasks={allTasks}
                  showPayments={showPayments}
                  redirectLocation={redirectLocation}
                  ntnToOpen={ntnToOpen}
                  ptoChoice={ptoChoice}
                />
              </div>
            ))
          ) : (
            <div className="tw-flex tw-flex-col tw-items-center tw-gap-4 tw-mt-10">
              <span>
                <NoFilteredClaimsView />
              </span>
            </div>
          )}
        </div>
      </ColumnContainer>

      <ColumnContainer id="summary-sidebar" cols={3}>
        <div className="tw-flex tw-flex-wrap sm:tw-flex-nowrap lg:tw-flex-col tw-gap-4 lg:tw-ml-2">
          {hasCoverage && isEmployerEnabled && (
            <MenuSection>
              <>
                <SidebarTitle>Supplemental benefits</SidebarTitle>
                <Separator color="green" />
                <ListContainer>
                  <ListItem>
                    <StyledLink to="/app/supplemental">
                      View my supplemental coverage
                    </StyledLink>
                  </ListItem>
                  <ListItem>
                    <StyledLink to="/app/supplemental">
                      File a supplemental benefit claim
                    </StyledLink>
                  </ListItem>
                  <ListItem>
                    <StyledExternalLink
                      href={
                        remoteCfg
                          ? remoteCfg[enums.RemoteConfigKeys.iServicesIDPURL]
                          : '#'
                      }
                      onClick={() => {
                        trackGA4Event({
                          action: 'click',
                          target:
                            'View my existing supplemental benefit claims',
                        });
                      }}
                    >
                      View my existing supplemental benefit claims
                    </StyledExternalLink>
                  </ListItem>
                </ListContainer>
              </>
            </MenuSection>
          )}
          {hasCoverage && !isEmployerEnabled && (
            <MenuSection>
              <>
                <SidebarTitle>Supplemental benefits</SidebarTitle>
                <Separator color="green" />
                <ListContainer>
                  <ListItem>
                    <StyledExternalLink href={mufmLink}>
                      View my supplemental coverage
                    </StyledExternalLink>
                  </ListItem>
                  <ListItem>
                    <StyledExternalLink href={mufmLink}>
                      File a supplemental benefit claim
                    </StyledExternalLink>
                  </ListItem>
                  <ListItem>
                    <StyledExternalLink href={mufmLink}>
                      View my existing supplemental benefit claims
                    </StyledExternalLink>
                  </ListItem>
                </ListContainer>
              </>
            </MenuSection>
          )}
          <MenuSection>
            <SidebarTitle>Update key dates</SidebarTitle>
            <Separator color="blue" />
            <ListContainer>
              <ListItem>
                <StyledLink to="/app/form/last-day-worked">
                  Last day of work
                </StyledLink>
              </ListItem>
              <ListItem>
                <StyledLink to="/app/form/return-to-work">
                  Return to work
                </StyledLink>
              </ListItem>
              <ListItem>
                <StyledLink to="/app/form/delivery-date">
                  Delivery date
                </StyledLink>
              </ListItem>
              <ListItem>
                <StyledLink to="/app/form/surgery-date">
                  Surgery details
                </StyledLink>
              </ListItem>
              <ListItem>
                <StyledLink to="/app/form/extension-request">
                  Request extension
                </StyledLink>
              </ListItem>
            </ListContainer>
          </MenuSection>
          <MenuSection>
            <SidebarTitle>Provide Unum details</SidebarTitle>
            <Separator color="blue" />
            <ListContainer>
              <ListItem>
                <StyledLink to="/app/form/intermittent-absence">
                  Report intermittent absence
                </StyledLink>
              </ListItem>
              <ListItem>
                <StyledLink to="/app/form/e-signature">
                  Sign e-Signature authorization forms
                </StyledLink>
              </ListItem>
              <ListItem>
                <StyledLink to="/app/upload">Upload a document</StyledLink>
              </ListItem>
              <ListItem>
                <StyledLink to="/app/form/physician-information">
                  Update physician information
                </StyledLink>
              </ListItem>
            </ListContainer>
          </MenuSection>
          <MenuSection>
            <SidebarTitle>Get support</SidebarTitle>
            <Separator color="blue" />
            <ListContainer>
              <ListItem>
                <div className="tw-relative tw-inline-block">
                  <ButtonMod
                    id="live-chat-button"
                    buttonStyle={enums.ButtonTypes.link}
                    aria-describedby={
                      !callCenter.open && showChatClosedTooltip
                        ? 'live-chat-closed-tooltip'
                        : undefined
                    }
                    onMouseEnter={() => setShowChatClosedTooltip(true)}
                    onMouseLeave={() => setShowChatClosedTooltip(false)}
                    onFocus={() => setShowChatClosedTooltip(true)}
                    onBlur={() => setShowChatClosedTooltip(false)}
                    onClick={() => {
                      if (callCenter.open) {
                        (globalThis as any).openLiveChat();
                      } else {
                        setShowChatClosedTooltip(true);
                      }
                    }}
                    className={`live-chat-${
                      callCenter.open ? 'online' : 'offline'
                    }`}
                  >
                    <span
                      style={{
                        color: callCenter.open ? '#037CB7' : '#929292',
                        textDecoration: 'none',
                      }}
                    >
                      Live chat
                    </span>
                  </ButtonMod>
                  {!callCenter.open && showChatClosedTooltip && (
                    <div
                      id="live-chat-closed-tooltip"
                      role="tooltip"
                      className="tw-absolute tw-bottom-full tw-left-0 tw-mb-2 tw-w-56 tw-rounded tw-bg-unm-primary02-400 tw-px-3 tw-py-2 tw-text-xs tw-font-normal tw-normal-case tw-leading-snug tw-text-white tw-shadow-lg tw-z-10"
                    >
                      <span>
                        Live chat is available Monday through Friday during our
                        business hours. Please check back then or schedule a
                        callback.
                      </span>
                      <span
                        className="tw-absolute tw-top-full tw-left-4 tw-bg-unm-primary02-400"
                        style={{
                          width: '0.5rem',
                          height: '0.5rem',
                          transform:
                            'translateY(-50%) translateX(1100%) rotate(45deg)',
                        }}
                      />
                    </div>
                  )}
                </div>
              </ListItem>
              <ListItem>
                <StyledLink to="/app/form/scheduled-callback">
                  Schedule a callback
                </StyledLink>
              </ListItem>
              <ListItem>
                <ButtonMod
                  id="survey-button"
                  buttonStyle={enums.ButtonTypes.link}
                  onClick={() => (globalThis as any)?.ShowSurveyFeedback?.()}
                >
                  Leave feedback
                </ButtonMod>
              </ListItem>
              <ListItem>
                <ButtonMod
                  id="cobrowse-button"
                  buttonStyle={enums.ButtonTypes.link}
                  onClick={() =>
                    (globalThis as any)?.Cobrowse.modal.openModal()
                  }
                >
                  Co-Browsing
                </ButtonMod>
              </ListItem>
            </ListContainer>
          </MenuSection>
        </div>
      </ColumnContainer>
    </GridContainer>
  ) : (
    <NoClaimsView />
  );
};

export default Summary;
